import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';
import WMTable from './WMTable/WMTable';
import EmptyResultsSetPlaceholder from './EmptyResultsSetPlaceholder/EmptyResultsSetPlaceholder';
import AppTableHeader from './AppTableHeader/AppTableHeader';
import { useFilters } from './helpers/useFilters';
import classes from './AppTable.module.scss';

import { AppTableContext } from './AppTableContext';
import AppTableRow from './AppTableRow/AppTableRow';
import { AppTableContextType, AppTableRowAnimateType, AppTableRowId, IAppTableProps, ITableData } from './model';
import AppTableActions from './AppTableActions/AppTableActions';
import TableHeaderWrapper from './TableHeaderWrapper/TableHeaderWrapper';
import { search } from './helpers/search';
import _some from 'lodash/some';
import { useFilterSortData } from './helpers/useFilterSortData';
import Pagination from './Pagination/Pagination';
import CircularProgress from '../../../assets/spinners/circular-progress.gif';
import { useTranslation } from 'react-i18next';

let bulk: $TSFixMe = [];

export async function triggerRowAnimation({
    animateType,
    ids,
    getValue,
    onChange,
    result,
    onDoneBulk
}: {
    animateType: AppTableRowAnimateType;
    ids: string[];
    getValue: () => Map<string, AppTableRowAnimateType>;
    onChange: (map: Map<string, AppTableRowAnimateType>) => void;
    result?: $TSFixMe;
    onDoneBulk?: (results: $TSFixMe[]) => $TSFixMe;
}) {
    const bulkItem = { current: { isAnimating: true, ids, data: result } };
    bulk.push(bulkItem);

    const map = new Map(getValue());
    ids.forEach(id => map.set(id, animateType));
    onChange(map);

    return new Promise<void>(resolve => {
        setTimeout(
            () => {
                bulkItem.current.isAnimating = false;

                // @ts-expect-error ts-migrate(2339) FIXME: Property 'current' does not exist on type 'never'.
                if (bulk.every(ref => !ref.current.isAnimating)) {
                    // @ts-expect-error ts-migrate(2339) FIXME: Property 'current' does not exist on type 'never'.
                    const bulkIds = bulk.map(ref => ref.current.ids).flat();
                    const map = new Map(getValue());
                    // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'id' implicitly has an 'any' type.
                    bulkIds.forEach(id => map.delete(id));
                    onChange(map);
                    // @ts-expect-error ts-migrate(2339) FIXME: Property 'current' does not exist on type 'never'.
                    onDoneBulk?.(bulk.map(ref => ref.current.data));
                    bulk = [];
                }

                resolve();
            },
            animateType === AppTableRowAnimateType.created ? 1200 : 800
        );
    });
}

const defaultTableState = Object.freeze<ITableData>({
    sorting: {
        dataKey: 'updatedAt',
        direction: 'desc'
    },
    selectedIds: [],
    filters: {},
    animateIds: new Map()
});

const defaultLoadingRows = new Array(5).fill({ id: null });

const AppTable = ({
    'data-testid': tableDataTestid,
    data,
    count,
    fetching,
    filtersFields,
    disabled: disabledProps = false,
    hideToolbar = false,
    tableName,
    searchTerm,
    onSearchTermChange,
    searchMethod = search,
    searchKeys = [],
    loadingRows = defaultLoadingRows,
    columns,
    classNames,
    actions,
    multipleRowsActions: multipleActions,
    tableData = defaultTableState,
    onTableDataChange,
    environmentId,
    emptyTableRenderer,
    isActiveRow,
    onRowClick,
    wmTableProps,
    ...extraDataAttributes
}: IAppTableProps) => {
    const { t } = useTranslation();
    const loading = !data;
    const disabled = loading || disabledProps || !!tableData.animateIds.size;
    const tableDataRef = useRef(tableData);
    tableDataRef.current = tableData;

    const { filtersList, setFilter } = useFilters(
        {
            data,
            columns,
            tableDataFilters: tableData.filters,
            onTableDataChange
        },
        {
            filtersFields
        }
    );

    const filteredSortedTableData = useFilterSortData(
        {
            data,
            tableData,
            searchTerm,
            searchMethod,
            searchKeys,
            environmentId,
            filtersList,
            columns
        },
        {
            skip: !!filtersFields
        }
    );

    const selectedIds = useMemo(() => new Set(tableData.selectedIds), [tableData.selectedIds]);
    const wmTableData = useMemo(() => (loading ? loadingRows : filteredSortedTableData), [loading, loadingRows, filteredSortedTableData]);

    const addProcessingRowIds = useCallback(
        async (ids: AppTableRowId[], type: AppTableRowAnimateType) => {
            const clone = new Map(tableDataRef.current.animateIds);
            ids.forEach(id => clone.set(id, type));
            onTableDataChange?.({ animateIds: clone });
        },
        [onTableDataChange]
    );

    const removeProcessingRowIds = useCallback(
        async (ids: AppTableRowId[], type: AppTableRowAnimateType) => {
            const clone = new Map(tableDataRef.current.animateIds);
            ids.forEach(id => {
                if (clone.get(id) === type) {
                    clone.delete(id);
                }
            });
            onTableDataChange?.({ animateIds: clone });
        },
        [onTableDataChange]
    );

    const context = useMemo(
        (): AppTableContextType => ({
            data: wmTableData,
            disabled,
            loading,
            addProcessingRowIds,
            removeProcessingRowIds,
            selectedIds,
            animateIds: tableData.animateIds,
            filtersList,
            setFilter,
            sorting: tableData.sorting,
            onTableDataChange,
            tableDataTestid,
            actions,
            multipleActions,
            environmentId,
            columns,
            tableName,
            searchTerm,
            onSearchTermChange
        }),
        [
            wmTableData,
            disabled,
            loading,
            addProcessingRowIds,
            removeProcessingRowIds,
            selectedIds,
            tableData.animateIds,
            tableData.sorting,
            filtersList,
            setFilter,
            onTableDataChange,
            tableDataTestid,
            actions,
            multipleActions,
            environmentId,
            columns,
            tableName,
            searchTerm,
            onSearchTermChange
        ]
    );

    useEffect(() => {
        const selectedIdsFilterDeleted = tableData.selectedIds.filter(id => _some(data, { id }));
        const updatedAnimateIds = new Map(tableData.animateIds);

        updatedAnimateIds.forEach((value, id, map) => {
            if (!_some(data, { id })) {
                map.delete(id);
            }
        });

        if (!onTableDataChange) return;

        if (selectedIdsFilterDeleted.length !== tableData.selectedIds.length) {
            onTableDataChange({ selectedIds: selectedIdsFilterDeleted });
        }

        if (updatedAnimateIds.size !== tableData.animateIds.size) {
            onTableDataChange({ animateIds: updatedAnimateIds });
        }
    }, [data, tableData.selectedIds, tableData.animateIds, onTableDataChange]);

    const checkedRows = wmTableData.filter((n: $TSFixMe) => selectedIds?.has?.(n.id));
    const showActions = !loading && !!wmTableData && wmTableData.length && checkedRows.length > 1;

    const footerRenderer = useMemo(
        () => (tableData?.pagination ? () => <Pagination page={tableData.page} count={count} limit={tableData.limit} /> : undefined),
        [count, tableData?.pagination, tableData.limit, tableData.page]
    );

    return (
        <AppTableContext.Provider value={context}>
            {fetching ? (
                <div className={classes.wmLoader}>
                    <img src={CircularProgress} alt={t('common.loading')} />
                </div>
            ) : null}

            {!hideToolbar && (
                <TableHeaderWrapper
                    tableDataTestid={tableDataTestid}
                    tableName={tableName}
                    disabled={disabled}
                    onSearchTermChange={onSearchTermChange}
                    searchTerm={disabled ? '' : searchTerm}
                />
            )}

            <div
                className={clsx([classes.tableContainer, classNames?.tableContainer], {
                    [classes.loading]: loading,
                    [classes.pagination]: tableData?.pagination
                })}
                table-data-loaded={(!loading).toString()}
                data-testid={tableDataTestid}
                {...extraDataAttributes}
            >
                <WMTable
                    className={classes.table}
                    paddingTableBodyClassName={clsx({
                        [classes.paddingTableActions]: showActions,
                        [classes.paddingTableBody]: !tableData?.pagination && !showActions
                    })}
                    data={wmTableData}
                    columns={columns}
                    emptyRenderer={
                        typeof emptyTableRenderer === 'string' ? (
                            <EmptyResultsSetPlaceholder text={emptyTableRenderer as string} />
                        ) : (
                            emptyTableRenderer
                        )
                    }
                    headerRenderer={(headerData: $TSFixMe) => <AppTableHeader headerData={headerData} disabled={disabled} />}
                    headerClassName={classes.headerClassName}
                    minWidth={100}
                    rowClassName={({ rowData }: $TSFixMe) =>
                        clsx(classes.row, {
                            [classes.fixedTopRow]: !!rowData.fixedTop,
                            [classes.onRowClick]: onRowClick,
                            [classes.loadingRow]: loading || tableData.animateIds?.has(rowData.id),
                            [classes.addedAnimate]: tableData.animateIds?.get(rowData.id) === AppTableRowAnimateType.created,
                            [classes.deletedAnimate]: tableData.animateIds?.get(rowData.id) === AppTableRowAnimateType.deleted,
                            [classes.modifiedAnimate]: tableData.animateIds?.get(rowData.id) === AppTableRowAnimateType.modified,
                            [classes.processing]: tableData.animateIds?.get(rowData.id) === AppTableRowAnimateType.process,
                            [classes.rowSelected]: isActiveRow?.(rowData)
                        })
                    }
                    rowEventHandlers={{
                        onClick: onRowClick
                    }}
                    rowProps={({ rowData }: $TSFixMe) => ({
                        'data-testid': `${tableDataTestid}-row-${rowData.id}`,
                        styles: { background: 'red' }
                    })}
                    cellProps={({ column, rowData }: $TSFixMe) => ({
                        'data-testid': `${tableDataTestid}-cell-${column.dataKey}-${rowData.id}`,
                        onClick: column.onClick
                    })}
                    rowRenderer={(data: $TSFixMe) => <AppTableRow {...data} />}
                    footerRenderer={footerRenderer}
                    {...wmTableProps}
                />
                {showActions ? <AppTableActions disabled={disabled} /> : null}
            </div>
        </AppTableContext.Provider>
    );
};

export default AppTable;
